import type React from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { Tooltip } from '@components/ui/Tooltips'
import { OverflowTextWithTooltip } from '@oldComponents/OverflowTextWithTooltip'

import { LinkIcon } from './SvgIcons'

const Wrapper = styled.div<{ $maxWidth: number }>`
  display: flex;
  align-items: center;
  max-width: ${({ $maxWidth }) => $maxWidth}px;
`

const PartnerOverflowTextWithTooltip = styled(OverflowTextWithTooltip)`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  & + .missing-warning {
    margin-left: 2px;
  }
`

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray[0]};
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  padding: 2px 6px;
  line-height: 1;

  * + & {
    margin-left: 4px;
  }
`

const ExpireBadge = styled(Badge)`
  background-color: ${({ theme }) => theme.colors.primary[170]};
`

const NotSeenYetBadge = styled(Badge)`
  background-color: ${({ theme }) => theme.colors.common.oceanBlue};
`

const NavBadge = styled(Badge)`
  background-color: ${({ theme }) => theme.colors.common.navBadge};
  white-space: nowrap;
`

interface ValueWithBadgesProps {
  children?: NonNullable<React.ReactNode>
  differentWarning?: React.ReactNode
  badges: {
    expireSoon?: boolean
    expired?: boolean
    notSeenYet?: boolean
    updated?: boolean
    isDeficient?: boolean
    isNavLink?: boolean
    isNavNoPreview?: boolean
  }
  maxWidth?: number
}

export default function ValueWithBadges({
  children,
  differentWarning,
  badges: {
    expireSoon = false,
    expired = false,
    notSeenYet = false,
    updated = false,
    isDeficient = false,
    isNavLink = false, // TODO "számlakép hiányos badge"
    isNavNoPreview = false,
  },
  maxWidth = 240,
}: ValueWithBadgesProps) {
  return (
    <Wrapper $maxWidth={maxWidth}>
      {children && <PartnerOverflowTextWithTooltip withPortal>{children}</PartnerOverflowTextWithTooltip>}
      {differentWarning}
      {isDeficient && (
        <ExpireBadge>
          <FormattedMessage id="badge.deficient.data" defaultMessage="Adathiányos" />
        </ExpireBadge>
      )}
      {notSeenYet && (
        <NotSeenYetBadge>
          <FormattedMessage id="badges.notSeenYet" defaultMessage="Új" />
        </NotSeenYetBadge>
      )}
      {updated && (
        <NavBadge>
          <FormattedMessage id="badges.updated" defaultMessage="Frissült" />
        </NavBadge>
      )}
      {(expireSoon || expired) && (
        <ExpireBadge>
          {expired ? (
            <FormattedMessage id="badges.expired" defaultMessage="Lejárt" />
          ) : (
            <FormattedMessage id="badges.expireSoon" defaultMessage="Lejár" />
          )}
        </ExpireBadge>
      )}
      {(isNavLink || isNavNoPreview) && (
        <Tooltip
          title={
            isNavLink ? (
              <FormattedMessage
                id="badges.tooltips.navLink"
                defaultMessage="Ezt a számlát már a NAV rendszeréből is szinkronizáltuk"
              />
            ) : (
              <FormattedMessage
                id="badges.tooltips.navPreview"
                defaultMessage="Ehhez a NAV-tól származó számlához még nem tartozik számlakép"
              />
            )
          }
        >
          <NavBadge as="div">
            {isNavLink ? (
              <>
                NAV&nbsp;
                <LinkIcon />
              </>
            ) : (
              <FormattedMessage id="badges.nav.noArtifact" defaultMessage="Nincs számlakép" />
            )}
          </NavBadge>
        </Tooltip>
      )}
    </Wrapper>
  )
}
